import { Button, Collapse, Grid } from "@mui/material";
import { Box } from "@mui/system";
import Findings from "../Components/ChapterOne/Findings";
import Samples from "../Components/ChapterOne/Samples";
import classes from "../Style/ChapterOne.scss";
import csv1 from '../Data/EUMACP/Overview/EU-MACP-Overview-Sample-origin-per-country-of-sampling-Stacked-bar-chart.csv'
import HorizontalBarChart from "../Components/Charts/HorizontalBarChart";
import ButtonsSelect from "../Components/Common/ButtonsSelect";
import MultiLineChart from "../Components/Charts/MultiLineChart";
import csv2 from '../Data/EUMACP/Overview/EU-MACP-Overview-MRL-Exceedances-rate-comparison-2014-2017-2020-Line-chart.csv';
import { useEffect, useState } from "react";
import HeatMapChart from "../Components/Charts/HeatMapChart";
import csv3 from "../Data/EUMACP/Overview/EU-MACP-Overview-No-of-quantified-residues-per-sample-XY-heatmap.csv";
import GroupBulletChart from "../Components/Charts/GroupBulletChart";
import csv4 from "../Data/EUMACP/Overview/EU-MACP-Overview-Reporting-country-response-to-EU-MACP-regulations-Bullet-chart.csv";
import csv5 from "../Data/EUMACP/Overview/EU-MACP-Overview-Summary-of-findings-Country-of-origin.csv";
import TableGrid from "../Components/ChapterOne/TableGrid";
import ChapterOneMap from "../Components/Maps/ChapterOneMap";
import mapJSON from '../Data/JSON/ChapterOneMap.json';
import mapCSV from '../Data/EUMACP/Overview/EU-MACP-Overview-No-of-samples-per-country-and-food-commodity.csv';
import ModalTrigger from "../Components/Common/ModalTrigger";
import CustomLink from "../Components/Common/CustomLink";
import { useNavigate } from "react-router-dom";
import PageRoutes from "../Config/PageRoutes";
import { usePapaParse } from "react-papaparse";
import neg1 from '../Assets/neg1.png';


const ChapterOne = () => {
    const aboveMRL = 'Above MRL';
    const belowMRL = 'Below or equal to MRL';

    const [mrlFilter, setMrlFilter] = useState('Above MRL');
    const [countryData, setCountryData] = useState(undefined);

    const navigate = useNavigate();

    const handleMRLExceedanceButtonClick = (evt) => {
        if (evt === 0) {
            if (aboveMRL !== mrlFilter) {
                setMrlFilter(aboveMRL);
            }
        } else {
            if (belowMRL !== mrlFilter) {
                setMrlFilter(belowMRL);
            }
        }
    }

    const handleCountryClick = (evt) => {
        setCountryData(evt);
        if (evt === undefined) {
            handleReadString();
        }
    }
    const [expanded, setExpanded] = useState(false);

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const goToChapterTwo = () => {
        navigate('../' + PageRoutes.CHAPTER_TWO);
    }

    const { readString } = usePapaParse();

    const handleReadString = () => {
        readString(mapCSV, {
            complete: (results, file) => {
                const initialData = { country: 'All', data: [] };
                const foodCommodities = results.data.map(d => d['Food commodity']);
                const unique = [...new Set(foodCommodities)];

                unique.forEach(fc => {
                    if (fc) {
                        initialData.data.push({ country: 'All', 'Food commodity': fc, Samples: 0 })
                    }
                });
                results.data.forEach(d => {
                    const found = initialData.data.find(da => da['Food commodity'] === d['Food commodity']);
                    if (found) {
                        found['Samples'] += d['Samples'];
                    }
                })
                setCountryData(initialData);
            },
            download: true,
            header: true,
            dynamicTyping: true,
            error: (error, file) => {
                console.log('Error while parsing:', error, file);
            },
        });
    }

    useEffect(() => {
        handleReadString();
    }, []);

    return (
        <Grid container direction="column" className="chapterOneWrapper">
            {/* First row */}
            <Grid item container direction={{ xs: 'column', sm: 'column', md: 'row' }} sx={{ marginTop: { xs: '50px', sm: '50px', md: '90px' } }}>
                <Grid item xs p={3} className="one-one">
                    <Box>
                        <span>EU MCAP RESULTS</span>
                        <h3>Results from the reporting countries </h3>
                        <p>In accordance with <CustomLink href="https://www.google.com/url?q=https://eur-lex.europa.eu/legal-content/EN/TXT/?uri%3DCELEX%253A32019R0533%23:~:text%3DCommission%2520Implementing%2520Regulation%2520(EU)%25202019,of%2520plant%2520and%2520animal%2520origin&sa=D&source=docs&ust=1645465497469635&usg=AOvVaw3W6tVLBIaPfDuVHJpR5FlW" text='Regulation (EU) 2019/533' />, EU Member States were requested to take a <ModalTrigger text='given number of samples by commodity' />. Nine countries were not able to reach this minimum number.  In most cases this was due to the COVID-19 pandemic. All 12 commodities reached the minimum number of 683 samples required to estimate a minimum 1% <ModalTrigger text='MRL exceedances' /> with a margin of error of 0.75%, except for rye grain. There was a significant increase (230%) in the number of poultry fat samples compared to 2017.</p>
                    </Box>
                    <Box sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}>
                        <Samples data={countryData} chunkSize={3} />
                    </Box>
                </Grid>
                <Grid item xs p={3} className="one-two">
                    <ChapterOneMap onCountryClick={handleCountryClick} json={mapJSON} csv={mapCSV} />
                </Grid>
                <Grid item xs p={3} sx={{ display: { xs: 'block', sm: 'block', md: 'none' } }} className="one-three">
                    <Samples data={countryData} chunkSize={4} />
                </Grid>
            </Grid>
            {/* First row */}

            {/* Second row */}
            <Grid item container direction={{ xs: 'column', sm: 'column', md: 'row' }} sx={{ marginTop: { xs: '100px', sm: '100px', md: '70px' } }}>
                <Grid item xs p={3} className="two-one">
                    <Box>
                        <h3>Results by sampling region</h3>
                        <p>A total of 12,077 samples were taken for the EU MACP. Of those, on average 60% were domestic, 22% came from other EU countries, 14% from third countries and 4% were of unknown origin. Countries where more than 80% of samples were domestic were Lithuania (100%), Spain (93%), Italy (87%) and France (81%). Those sampling more than 30% from third countries were Romania (42%) and Finland (31%). Belgium (28%), Ireland (12%) and Iceland (11%) had more than 10% of samples of unknown origin.</p>
                    </Box>
                </Grid>
                <Grid item xs p={3} className="two-two">
                    <HorizontalBarChart
                        csv={csv1}
                        title='Sample origin % per country of sampling'
                        yField='Reporting country'
                        xField='Region proportion per country'
                        stackField='Pesticide region'
                    />
                </Grid>
            </Grid>
            {/* Second row */}
            {/* Third row */}
            <Grid item container direction='column' sx={{ marginTop: { xs: '100px', sm: '100px', md: '150px' }, backgroundColor: '#F9F9F9' }} alignContent="center">
                <Grid item xs p={3} className="three-one">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} className="three-one-inner">
                        <h3>Overall EU MACP findings</h3>
                        <p>In 68.5% (8,278 out of 12,077) of samples analysed no <ModalTrigger text='quantifiable levels' />  of residues were reported (residues were below the <ModalTrigger text='LOQ' />). The number of samples with pesticide residues within legally permitted levels (at or <ModalTrigger text='above the LOQ but below or at the MRL' />) was 3,590 (29.7%). MRLs were exceeded in 1.7% (209) of samples, of which 0.9% (113) were found to be <ModalTrigger text='non-compliant' /> based on the measurement uncertainty.</p>
                    </Box>
                </Grid>
                <Grid item xs p={3} className="three-two">
                    <Findings chartImg={neg1} />
                </Grid>
            </Grid>
            {/* Third row */}
            {/* Fourth row */}
            <Grid item container direction={{ xs: 'column', sm: 'column', md: 'row' }} sx={{ marginTop: { xs: '50px', sm: '50px', md: '140px' } }}>
                <Grid item xs p={3} className="four-one">
                    <Box>
                        <h3>MRL exceedance rate comparison between 2020, 2017 and 2014</h3>
                        <p>The EU MACP covers the most consumed food products in Europe. The listed products are distributed across a <ModalTrigger text='three-year cycle' />, so that every three years the same products are analysed.</p>
                        <p>A comparison between the results for 2017 and 2020 shows that the overall <ModalTrigger text='MRL exceedance rate' /> rose from 1.7% in 2017 to 2.1% in 2020. Among individual food commodities, MRL exceedance rates rose from 2014 to 2017 and to 2020 in rice, oranges, pears and poultry fat.</p>
                        <p>An increased trend from 2017 to 2020 was also observed in dried beans, kiwi fruits (green, red, yellow) and cauliflower.  Exceedance rates fell for carrots, potatoes, rye and onions between 2014 and 2020. Bovine liver remained steady with no MRL exceedances.</p>
                    </Box>
                </Grid>
                <Grid item xs p={3} sx={{ width: '100%' }} className="four-two">
                    <Box>
                        <ButtonsSelect firstButton='>MRL' secondButton='≤MRL' onButtonClick={handleMRLExceedanceButtonClick} />
                        <MultiLineChart legendPosition='right' title='MRL Exceedances rate comparison (2014, 2017, 2020)' csv={csv2} filter={mrlFilter} />
                    </Box>
                </Grid>
            </Grid>
            {/* Fourth row */}
            {/* Fifth row */}
            <Grid item container direction={{ xs: 'column', sm: 'column', md: 'row' }} sx={{ marginTop: { xs: '50px', sm: '50px', md: '140px' } }}>
                <Grid item xs p={3} className="five-one" pt={7} pb={7} pl={4} pr={4}>
                    <Box>
                        <h3>Quantified residues per sample</h3>
                        <p>Of the 12,077 samples, 3,799 had quantified results (31.5%) and 2,199 samples (18%) had more than 1 pesticide quantified. By food product, oranges (762 samples), followed by pears (696), carrots (250) and rice (134) had the highest number of <ModalTrigger text='samples with multiple residues' />. The highest frequency of multiple residues was found in rice in a sample with origin unknown where 15 different pesticides were quantified followed by another sample in pear where 14 pesticides all <ModalTrigger text='below the MRL' /> were quantified.</p>
                    </Box>
                </Grid>
                <Grid item xs p={3} className="five-two">
                    <HeatMapChart
                        title='No. of quantified residues per sample'
                        csv={csv3}
                        yAxisName='Food commodity'
                        xAxisName='Number of quantifications'
                        valueField='Proportion per food commodity'
                    />
                </Grid>
            </Grid>
            {/* Fifth row */}
            {/* Sixth row */}
            <Grid item container direction={{ xs: 'column', sm: 'column', md: 'row' }} sx={{ marginTop: { xs: '50px', sm: '50px', md: '140px' } }}>
                <Grid item xs={12} p={3} className="six-one">
                    <h3>Reporting country response to EU MACP Regulation</h3>
                </Grid>
                <Grid item xs p={3} sx={{ width: '50%' }} className="six-three" display={{ xs: 'none', sm: 'none', md: 'block' }}>
                    <GroupBulletChart first title='Number of samples per pesticide against EU MACP target samples' part={1} csv={csv4} />
                </Grid>
                <Grid item xs p={3} sx={{ width: '50%' }} className="six-four" display={{ xs: 'none', sm: 'none', md: 'block' }}>
                    <GroupBulletChart second title='Number of samples per pesticide against EU MACP target samples' part={2} csv={csv4} />
                </Grid>
                <Grid item xs={12} p={3} display={{ xs: 'flex', sm: 'flex', md: 'none' }} flexDirection='column' alignItems='center' justifyContent='center'>
                    <Collapse style={{ width: '100%' }} collapsedSize={200} in={expanded} timeout="auto">
                        <GroupBulletChart title='Number of samples per pesticide against EU MACP target samples' csv={csv4} />
                    </Collapse>
                    <Button style={{ marginTop: '1rem', }} className="footerButton" variant="outlined" onClick={handleExpandClick}>{expanded ? 'Show less' : 'Load more'}</Button>
                </Grid>
            </Grid>
            {/* Sixth row */}
            {/* Seventh row */}
            <Grid item container direction='row' sx={{ marginTop: { xs: '50px', sm: '50px', md: '140px' } }}>
                <Grid item xs p={3} className="seven-one">
                    <TableGrid csv={csv5} />
                </Grid>
            </Grid>
            {/* Seventh row */}
            {/* Eighth row */}
            <Grid item container direction='column' sx={{ marginTop: { xs: '50px', sm: '50px', md: '165px' } }} alignContent="center">
                <Grid item xs p={3} className="-one">
                    <Box className="nine-one-inner">
                        <p style={{ textAlign: 'center' }}><b>Continue exploring</b></p>
                        <Button onClick={goToChapterTwo} className="footerButton" variant="outlined">National Programmes Results</Button>
                    </Box>
                </Grid>
            </Grid>
            {/* Eighth row */}
        </Grid >
    );
}

export default ChapterOne;